import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildDays,
  buildSlotColumns,
  calculatePosition,
  getHourLabel,
  HOUR_HEIGHT,
  parseOffsetMinutes,
} from '@/components/schedule-layout';
import { TimeSlotDetailDialog } from '@/components/time-slot-detail-dialog';
import type { TimeSlot } from '@/types';

// `?slot=<slug>` opens a block's detail dialog; the slot's id works too.
const SLOT_PARAM = 'slot';

function setSlotParam(slug: string | null) {
  const url = new URL(window.location.href);
  if (slug) url.searchParams.set(SLOT_PARAM, slug);
  else url.searchParams.delete(SLOT_PARAM);
  window.history.replaceState(window.history.state, '', url);
}

interface ScheduleProps {
  timeSlots: TimeSlot[];
  startsAt: string | Date;
  endsAt: string | Date;
  timezoneOffset: string;
  timeZone?: string;
  className?: string;
  titleFont?: string;
}

export function Schedule({
  timeSlots,
  startsAt,
  endsAt,
  timezoneOffset,
  timeZone,
  className = '',
  titleFont = 'font-title',
}: ScheduleProps) {
  const eventStart = useMemo(() => new Date(startsAt), [startsAt]);
  const eventEnd = useMemo(() => new Date(endsAt), [endsAt]);
  const offsetMinutes = useMemo(
    () => parseOffsetMinutes(timezoneOffset),
    [timezoneOffset],
  );
  const days = useMemo(
    () => buildDays(eventStart, eventEnd, offsetMinutes),
    [eventStart, eventEnd, offsetMinutes],
  );

  const isWithinEventHours = (date: Date): boolean =>
    date >= eventStart && date <= eventEnd;

  const getCurrentTimePosition = (): {
    day: number;
    position: number;
  } | null => {
    const now = new Date();
    if (!isWithinEventHours(now)) return null;
    for (let i = 0; i < days.length; i++) {
      const dayStart = new Date(`${days[i].date}T00:00:00${timezoneOffset}`);
      const dayEnd = new Date(`${days[i].date}T23:59:59${timezoneOffset}`);
      if (now >= dayStart && now <= dayEnd) {
        return { day: i, position: calculatePosition(now, dayStart) };
      }
    }
    return null;
  };

  const [currentTime, setCurrentTime] = useState<{
    day: number;
    position: number;
  } | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(
    null,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  const handleTimeSlotClick = (slot: TimeSlot) => {
    setSelectedTimeSlot(slot);
    setIsDialogOpen(true);
    setSlotParam(slot.slug);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) setSlotParam(null);
  };

  // Opening a shared link: show the slot and bring it into view. Marking the scroll as
  // done keeps the "jump to now" effect below from pulling the page away from it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: read the URL once, on mount
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get(SLOT_PARAM);
    const slot =
      wanted && timeSlots.find((s) => s.slug === wanted || s.id === wanted);
    if (!slot) return;

    setSelectedTimeSlot(slot);
    setIsDialogOpen(true);
    hasScrolledRef.current = true;

    const start = new Date(slot.startTime);
    const dayIndex = days.findIndex((day) => {
      const dayStart = new Date(`${day.date}T00:00:00${timezoneOffset}`);
      const dayEnd = new Date(`${day.date}T23:59:59${timezoneOffset}`);
      return start >= dayStart && start <= dayEnd;
    });
    if (dayIndex === -1 || !scrollContainerRef.current) return;

    const dayStart = new Date(`${days[dayIndex].date}T00:00:00${timezoneOffset}`);
    const rect = scrollContainerRef.current.getBoundingClientRect();
    const top = rect.top + window.scrollY + calculatePosition(start, dayStart);
    window.scrollTo({ top: Math.max(0, top - 200), behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const updateCurrentTime = () => {
      setCurrentTime(getCurrentTimePosition());
    };

    updateCurrentTime();
    const interval = setInterval(updateCurrentTime, 60000);

    return () => clearInterval(interval);
    // biome-ignore lint/correctness/useExhaustiveDependencies: same deps as the Next component
  }, [days, timezoneOffset]);

  useEffect(() => {
    if (currentTime && scrollContainerRef.current && !hasScrolledRef.current) {
      const rect = scrollContainerRef.current.getBoundingClientRect();
      const scrollPosition =
        rect.top + window.scrollY + currentTime.position - 200;
      window.scrollTo({ top: Math.max(0, scrollPosition), behavior: 'smooth' });
      hasScrolledRef.current = true;
    }
  }, [currentTime]);

  const slotsByDay = days.map((day) => {
    const dayStart = new Date(`${day.date}T00:00:00${timezoneOffset}`);
    const dayEnd = new Date(`${day.date}T23:59:59${timezoneOffset}`);

    return timeSlots.filter((slot) => {
      const slotStart = new Date(slot.startTime);
      return slotStart >= dayStart && slotStart <= dayEnd;
    });
  });

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const gridTemplate = `80px ${days.map(() => '1fr').join(' ')}`;
  const minWidth = `${80 + days.length * 240}px`;

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="overflow-x-auto border-2 border-primary bg-background md:overflow-x-visible">
        <div
          className="sticky top-0 z-10 grid border-primary border-b-2 bg-background"
          style={{ gridTemplateColumns: gridTemplate, minWidth }}
        >
          <div className="sticky left-0 z-20 border-primary border-r-2 bg-background p-4">
            <span
              className={`font-bold ${titleFont} text-primary text-sm md:text-base`}
            >
              Hora
            </span>
          </div>
          {days.map((day) => (
            <div
              key={day.date}
              className="border-primary border-r-2 p-4 text-center last:border-r-0"
            >
              <h3
                className={`font-bold ${titleFont} text-primary text-sm md:text-base lg:text-lg`}
              >
                <span className="hidden sm:inline">{day.label}</span>
                <span className="sm:hidden">{day.shortLabel}</span>
              </h3>
            </div>
          ))}
        </div>

        <div ref={scrollContainerRef} className="relative">
          <div
            className="grid"
            style={{ gridTemplateColumns: gridTemplate, minWidth }}
          >
            <div className="sticky left-0 z-10 border-primary border-r-2 bg-background">
              {hours.map((hour) => (
                <div
                  key={hour}
                  className="border-primary border-b"
                  style={{ height: `${HOUR_HEIGHT}px` }}
                >
                  <div className="p-2 font-mono text-primary text-xs md:text-sm">
                    {getHourLabel(hour)}
                  </div>
                </div>
              ))}
            </div>

            {days.map((day, dayIndex) => {
              const dayStart = new Date(
                `${day.date}T00:00:00${timezoneOffset}`,
              );
              const slots = slotsByDay[dayIndex];

              const slotColumns = buildSlotColumns(slots, dayStart);

              return (
                <div
                  key={day.date}
                  className="relative border-primary border-r-2 last:border-r-0"
                >
                  {hours.map((hour) => {
                    const hourDate = new Date(
                      `${day.date}T${getHourLabel(hour)}${timezoneOffset}`,
                    );
                    const isOutsideEvent = !isWithinEventHours(hourDate);

                    return (
                      <div
                        key={hour}
                        className={`border-primary border-b ${
                          isOutsideEvent ? 'bg-primary/5' : ''
                        }`}
                        style={{ height: `${HOUR_HEIGHT}px` }}
                      />
                    );
                  })}

                  {slotColumns.map(
                    ({ position, slots: overlappingSlots, slotWidth }) => {
                      return overlappingSlots.map((slot, index) => {
                        const start = new Date(slot.startTime);
                        const end = new Date(slot.endTime);
                        const duration =
                          (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                        const height = duration * HOUR_HEIGHT;

                        return (
                          <button
                            key={slot.id}
                            type="button"
                            onClick={() => handleTimeSlotClick(slot)}
                            className="absolute cursor-pointer border-2 border-background p-2 text-left transition-all hover:z-10 hover:scale-105"
                            style={{
                              top: `${position}px`,
                              left: `${index * slotWidth}%`,
                              width: `${slotWidth}%`,
                              height: `${height}px`,
                              backgroundColor: slot.color,
                            }}
                          >
                            <div className="flex h-full flex-col gap-1 overflow-hidden">
                              <h4
                                className={`line-clamp-1 font-bold ${titleFont} text-background text-xs md:text-sm`}
                              >
                                {slot.title}
                              </h4>
                              {height > 40 && slot.location && (
                                <p className="line-clamp-1 font-mono text-background/90 text-xs">
                                  📍 {slot.location}
                                </p>
                              )}
                              {height > 60 && slot.description && (
                                <p className="line-clamp-2 font-mono text-background/80 text-xs">
                                  {slot.description}
                                </p>
                              )}
                              {height > 80 && slot.target.length > 0 && (
                                <div className="mt-auto flex flex-wrap gap-1">
                                  {slot.target.map((target) => (
                                    <span
                                      key={target}
                                      className="bg-background/20 px-1 py-0.5 font-mono text-background text-xs uppercase"
                                    >
                                      {target}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      });
                    },
                  )}

                  {currentTime && currentTime.day === dayIndex && (
                    <div
                      className="absolute left-0 z-20 w-full border-red-500 border-t-2"
                      style={{ top: `${currentTime.position}px` }}
                    >
                      <div className="-top-2 -left-2 absolute h-4 w-4 rounded-full bg-red-500" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <TimeSlotDetailDialog
        timeSlot={selectedTimeSlot}
        open={isDialogOpen}
        onOpenChange={handleDialogOpenChange}
        timeZone={timeZone}
        shareUrl={
          selectedTimeSlot && typeof window !== 'undefined'
            ? `${window.location.origin}${window.location.pathname}?${SLOT_PARAM}=${encodeURIComponent(selectedTimeSlot.slug)}`
            : undefined
        }
      />
    </div>
  );
}
